/**
 * Hero: views of the entity, the same four for every class: along the track, as a graph (the
 * network around it), on the map, and its data model. A view
 * is offered when its row (or the geometry) has something to draw. A view chosen by the user
 * fades in; the first one does not.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingState, Segmented } from '@bn-apps/dim-ui-react';
import type { EntityViewModel } from '@hooks/useEntityView';
import { heroKeys, pickLang } from '@utils/viewShapes.utils';
import type { IncomingGroup } from '@services/entityView.service';
import { widgetFor } from '../widgets/registry';
import type { WidgetContext } from '../widgets/types';
import { uriText } from '../widgets/values';
import DataModel from './DataModel';
import { EntityGraph } from './Graph';
import { KeyFacts, pageTitle } from './Identity';
import styles from '../EntityTemplate.module.scss';

const EntityMap = lazy(() => import('../../EntityMap'));

const setting0 = (model: EntityViewModel, name: string) => model.nodeShapes.find((shape) => shape.settings?.[name]?.length)?.settings[name][0];

export type HeroView = 'along' | 'graph' | 'map' | 'model';

interface HeroProps {
  model: EntityViewModel;
  context: WidgetContext;
  wkt: string | null;
  chosen: HeroView | null;
  onChoose: (view: HeroView) => void;
  incoming: IncomingGroup[];
  /** At the end of the tab row: the minimise button. */
  actions?: ReactNode;
}

const Hero = ({ model, context, wkt, chosen, onChoose, incoming, actions }: HeroProps): JSX.Element | null => {
  const { t } = useTranslation();
  // The rows the class names in dview:hero; without it, every row with a hero viewer
  const chosenKeys = heroKeys(model.nodeShapes);
  const heroRows = model.allProperties
    .filter((property) => property.viewer && widgetFor(property.viewer).hero)
    .filter((property) => !chosenKeys || (property.key !== null && chosenKeys.includes(property.key)))
    .sort((a, b) => (chosenKeys ? chosenKeys.indexOf(a.key!) - chosenKeys.indexOf(b.key!) : 0));
  const rendered: Partial<Record<HeroView, JSX.Element>> = {};
  let network: ReactNode = null;
  for (const property of heroRows) {
    const entry = widgetFor(property.viewer!);
    const node = entry.render({ property, values: model.values[property.id] ?? [], context });
    // A class's network drawing (topology, a track's neighbours) is the second option of the Graph tab
    if (node && entry.hero === 'graph') network = network ?? node;
    // Rows of one view sit side by side, in dview:hero order: a switch's fork beside its strip
    else if (node && entry.hero)
      rendered[entry.hero] = rendered[entry.hero] ? (
        <div className={styles.heroSplit}>
          {rendered[entry.hero]}
          <div className={styles.heroSplitMain}>{node}</div>
        </div>
      ) : (
        <>{node}</>
      );
  }
  rendered.graph = <EntityGraph model={model} context={context} network={network} />;
  if (wkt) {
    rendered.map = (
      <Suspense fallback={<LoadingState />}>
        <div className={styles.heroMap}>
          <EntityMap wkt={wkt} />
        </div>
      </Suspense>
    );
  }
  rendered.model = <DataModel model={model} context={context} incoming={incoming} />;
  // The same four tabs for every class, each shown when it has something to draw; the class's
  // dview:firstView picks the one a page opens on (the reader's last choice wins)
  const first = setting0(model, 'firstView');
  const views = (['along', 'graph', 'map', 'model'] as const).filter((view) => rendered[view]);
  const opening = first && views.includes(first as HeroView) ? (first as HeroView) : views[0];
  const view = chosen && views.includes(chosen) ? chosen : opening;

  if (!view) return null;

  return (
    <section id="entity-hero" className={styles.hero} aria-labelledby="entity-hero-title">
      <div className={styles.heroBar}>
        <h2 id="entity-hero-title" className={styles.heroTitle}>
          {t(`entity.template.hero.title.${view}`)}
        </h2>
        {/* The views and the minimise button on the right */}
        <span className={styles.heroTools}>
          {views.length > 1 && (
            <Segmented
              label={t('entity.template.hero.views')}
              hideLabel
              size="sm"
              value={view}
              onChange={onChoose}
              options={views.map((option) => ({ value: option, label: t(`entity.template.hero.${option}`) }))}
            />
          )}
          {actions}
        </span>
      </div>
      {/* The object's key figures above every drawing, the same pills as under the title */}
      <KeyFacts model={model} context={context} stretch={context.stretch ?? null} title={pageTitle(uriText(model.uri, context), model.nodeShapes.map((shape) => pickLang(shape.label, context.language)).find(Boolean) ?? undefined)} />
      <div key={view} className={styles.heroBody} data-enter={chosen ? view : undefined}>
        {rendered[view]}
      </div>
    </section>
  );
};

export default Hero;
